/**
 * Shared types + pure helpers for People DB background import jobs (LEV-927).
 *
 * Both the CSV import (LinkedIn-scale, up to 30k rows) and the Apollo "Search
 * for more" prospecting flow run as a single durable Inngest job, tracked by
 * the `PeopleImportJob` row. This module is the cross-cutting contract shared
 * by the tRPC router (start/cancel/getJob), the Inngest job, and the client —
 * no React, no DB, no Inngest imports so every layer can use it.
 *
 * Cancellation contract: ONLY an explicit cancel (which sets
 * `cancelRequestedAt`) stops a running job. Closing the modal or the app does
 * NOT cancel — the job runs to completion. The job checks `cancelRequestedAt`
 * via `shouldCancel` at BATCH BOUNDARIES (before and after each ≤300-row
 * batch) and stops gracefully, keeping partial results. The CSV bulk path
 * inserts a whole batch in one transaction, so there is no intra-batch cancel
 * point — a cancel takes effect at the next batch boundary (≤300 rows / a few
 * seconds).
 */
import type { PipelineKind } from "@/constants/pipelines";
import type { CsvColumnMapping } from "@/lib/people/csvImport";

/** Inngest event that triggers the parameterized import job. */
export const PEOPLE_IMPORT_REQUESTED_EVENT = "people/import.requested" as const;

/**
 * Deep-link query param (LEV-1044 note 1): import notifications link to
 * `/orbit?importHistory=open` so a click lands on the Orbit People page with
 * the Import history modal already open. Shared between the server-side URL
 * builder (`importHistoryUrl`) and the client (`PeopleDbView` reads it on
 * mount), so both agree on the exact key + value.
 */
export const IMPORT_HISTORY_QUERY_PARAM = "importHistory" as const;
export const IMPORT_HISTORY_QUERY_VALUE = "open" as const;

/**
 * Deep-link query param (LEV-1141) that opens ONE import flow's modal on
 * arrival at `/orbit`, e.g. `/orbit?import=linkedin`. Added so the "More ways
 * to fill your Orbit" links in the import email land the founder directly in
 * the flow they clicked instead of on the Orbit page with instructions to go
 * find the Import menu themselves.
 *
 * Deliberately NOT `CsvImportSource`: that type spells the plain CSV upload
 * `"import"`, which reads as nonsense in a URL (`?import=import`). These are
 * the user-facing target names; `IMPORT_DEEP_LINK_TO_SOURCE` maps them back.
 */
export const IMPORT_SOURCE_QUERY_PARAM = "import" as const;

export const IMPORT_DEEP_LINK_TARGETS = ["linkedin", "google", "csv"] as const;

export type ImportDeepLinkTarget = (typeof IMPORT_DEEP_LINK_TARGETS)[number];

/** URL target → the `CsvImportSource` the resulting import is attributed to. */
export const IMPORT_DEEP_LINK_TO_SOURCE: Record<
  ImportDeepLinkTarget,
  CsvImportSource
> = {
  linkedin: "linkedin",
  google: "google",
  csv: "import",
};

/** Inverse of `IMPORT_DEEP_LINK_TO_SOURCE`, for building links from a source. */
export const IMPORT_SOURCE_TO_DEEP_LINK: Record<
  CsvImportSource,
  ImportDeepLinkTarget
> = {
  linkedin: "linkedin",
  google: "google",
  import: "csv",
};

/**
 * Narrow an untrusted `?import=` value to a known target. Returns null for
 * anything unrecognized so a hand-edited URL opens no modal rather than
 * throwing or opening the wrong flow.
 */
export function parseImportDeepLinkTarget(
  value: string | null | undefined,
): ImportDeepLinkTarget | null {
  if (!value) return null;
  return (
    IMPORT_DEEP_LINK_TARGETS.find((target) => target === value) ?? null
  );
}

/** Discriminant for the import sources (CSV upload, Apollo search, Orbit ICP). */
export type PeopleImportKind = "csv" | "apollo" | "orbit";

/**
 * Entity-source attribution for People/Companies created by a CSV import
 * (LEV-1004, extended for Google in LEV-1044). `"import"` is the default for a
 * plain "Add from CSV" upload; the guided flows stamp `"linkedin"` / `"google"`.
 * Drives the Source badge, the "Added to Orbit" activity qualifier, and the
 * per-source import-history view.
 */
export type CsvImportSource = "import" | "linkedin" | "google";

/** Lifecycle status persisted on the `PeopleImportJob` row. */
export type PeopleImportStatus =
  | "pending"
  | "running"
  | "completed"
  | "canceled"
  | "failed";

/** Statuses where the job is no longer doing work. */
export const TERMINAL_IMPORT_STATUSES: ReadonlySet<PeopleImportStatus> = new Set(
  ["completed", "canceled", "failed"],
);

export function isTerminalImportStatus(status: string): boolean {
  return TERMINAL_IMPORT_STATUSES.has(status as PeopleImportStatus);
}

/**
 * Rows processed per batch. Smaller batches give more frequent progress
 * updates and faster cancel response; larger batches amortize the
 * per-batch overhead (dedupe query + transaction open/close).
 */
export const IMPORT_BATCH_SIZE = 50;

/**
 * A non-terminal import job whose heartbeat (`updatedAt`) hasn't advanced in
 * this long is treated as ABANDONED (LEV-1068). Two ways a job orphans:
 *   - PENDING forever — the Inngest runner never claimed it (a lost/failed
 *     `inngest.send`, or the worker was offline at enqueue time). `updatedAt`
 *     stays pinned at `createdAt`.
 *   - RUNNING forever — the Inngest run died mid-import and stopped writing
 *     progress, so `updatedAt` freezes.
 * A healthy running job bumps `updatedAt` on every per-row progress write
 * (sub-second), and Inngest normally delivers a pending event within seconds,
 * so 5 minutes of no movement is unambiguously stuck. Recovering it
 * (`isStaleImportJob`) is what stops one orphaned row from locking the founder
 * out of EVERY future import via the `startImport` idempotency guard.
 */
export const IMPORT_STALE_AFTER_MS = 5 * 60_000;

/**
 * Terminal error stamped on an abandoned import job when `startImport` sweeps it
 * (LEV-1068). Shown in the Import-history list so the founder sees the attempt
 * didn't go through (rather than the row silently vanishing) and can retry.
 */
export const ABANDONED_IMPORT_ERROR =
  "Import didn't start — the background worker didn't pick it up. Please try again.";

/**
 * Whether a non-terminal import job has gone stale — no worker progress within
 * `IMPORT_STALE_AFTER_MS`. Terminal jobs are never stale (they're done). Uses
 * `updatedAt` as the heartbeat: the pending→running claim and every progress
 * write bump it, so only an unclaimed (pending) or orphaned (running) job lets
 * it age past the threshold. Pure + `now`-injectable so it's deterministically
 * testable.
 */
export function isStaleImportJob(
  job: { status: string; updatedAt: Date | string },
  now: Date = new Date(),
): boolean {
  if (isTerminalImportStatus(job.status)) return false;
  const updatedAtMs = new Date(job.updatedAt).getTime();
  if (Number.isNaN(updatedAtMs)) return false;
  return now.getTime() - updatedAtMs > IMPORT_STALE_AFTER_MS;
}

/**
 * Poll cadences (ms) for the Orbit `activeImportJob` query (LEV-1068). While a
 * job is in flight we poll FAST for live progress; while idle we still poll —
 * SLOWLY — instead of stopping, so an Orbit page that's been sitting open
 * discovers an import started in another tab/device (or one this page's
 * invalidate missed) within a few seconds and locks the Import menu + shows the
 * strip, rather than staying stale until a manual reload.
 */
export const ACTIVE_IMPORT_POLL_MS = 1500;
export const ACTIVE_IMPORT_IDLE_POLL_MS = 8000;

/**
 * Private Vercel Blob object metadata for an in-progress CSV import (LEV-1088).
 * Stored in `CsvImportPayload.blob` so upload transport stays out of the form
 * request body. The server fetches it once, then persists `csvText` for the
 * batch worker.
 */
export interface CsvImportBlobMetadata {
  url: string;
  pathname: string;
  size: number;
  contentType: string | null;
}

/** Raw input for a CSV import: temporary CSV text + Blob metadata + mapping. */
export interface CsvImportPayload {
  kind: "csv";
  /**
   * Raw CSV text fetched server-side from Blob at job kickoff.
   * Nullable because terminal cleanup sets it to `null` after processing.
   */
  csvText: string | null;
  /**
   * Private Vercel Blob object holding the raw CSV during processing (LEV-1088).
   * Nullable because terminal finalization sets it to `null` after the worker
   * has consumed and deleted the blob. New pending/running CSV jobs always have
   * a non-null blob.
   */
  blob: CsvImportBlobMetadata | null;
  /** User-confirmed column mapping from the preview step. */
  mapping: CsvColumnMapping;
  /** When set, imported People are tagged to this project. */
  projectId?: string;
  /** When set (with projectId), each Person becomes a pipeline entry. */
  pipeline?: PipelineKind;
  /** Target stage key; resolved server-side to the first stage when omitted. */
  stageKey?: string;
  /**
   * LEV-1004: entity source attribution for the created People + Companies.
   * `"linkedin"` for the LinkedIn Connections.csv flow, `"google"` for the
   * Google Contacts flow (LEV-1044), `"import"` (the default when omitted) for a
   * plain "Add from CSV" upload. Drives the Source badge, the "Added to Orbit"
   * activity qualifier, and the per-source import-history view.
   */
  source?: CsvImportSource;
}

/** Raw input for an Apollo "Search for more" run. */
export interface ApolloImportPayload {
  kind: "apollo";
  projectId: string;
  pipeline: PipelineKind;
}

/**
 * Raw input for an Orbit ICP prospecting run (LEV-1003). Same shape as the
 * Apollo payload — scans the founder's existing Orbit People instead of Apollo.
 */
export interface OrbitImportPayload {
  kind: "orbit";
  projectId: string;
  pipeline: PipelineKind;
}

export type PeopleImportPayload =
  | CsvImportPayload
  | ApolloImportPayload
  | OrbitImportPayload;

/**
 * One progress milestone (mirrors the Apollo `SearchForMoreProgress` shape so
 * the existing modal stepper renders identically). Persisted to
 * `PeopleImportJob.progressTrail` so a reopened modal can replay it.
 */
export interface ImportProgressEvent {
  step: string;
  message: string;
  total?: number;
  remaining?: number;
  created?: number;
  index?: number;
}

/**
 * One row in the Orbit import-history list (LEV-1044). Mirrors the
 * `people.listImportHistory` query item shape: the `PeopleImportJob` row exists
 * from kickoff (status "pending"), so the list shows in-progress imports with
 * their live progress fields, not just finished ones. Dates are pre-stringified
 * ISO so the client never touches a `Date`.
 */
export interface ImportHistoryItem {
  id: string;
  source: CsvImportSource;
  status: string;
  stepLabel: string | null;
  processed: number;
  total: number;
  addedCount: number;
  skippedCount: number;
  /** Breakdown of WHY rows were skipped (LEV-1044 n3). Null for pre-feature rows
   * and imports that finished before this column existed. */
  skipReasons: SkipReasons | null;
  error: string | null;
  createdAt: string;
  startedAt: string | null;
  finishedAt: string | null;
}

/**
 * Why rows were skipped during a CSV import (LEV-1044 n3). Persisted on the job
 * (`PeopleImportJob.skipReasons`) and surfaced in the import-history status line.
 *   - `missingData`: row had no usable name/data and was dropped before create.
 *   - `duplicate`: the person already exists in Orbit (same email / LinkedIn) OR
 *     repeated within the same file.
 *   - `error`: a per-row exception during create.
 * The three buckets sum to `skippedCount`.
 *
 * Pipeline-board imports also record which rows got NO card, as subsets of
 * `duplicate` (so they never change `skippedCount`):
 *   - `alreadyOnBoard`: the person was already on this pipeline, so no entry
 *     was created for the row.
 *   - `repeatedInFile`: the row repeated an earlier row of the same batch and
 *     was collapsed before create (its person got at most one card).
 * Rows written before these keys existed read them as 0.
 */
export interface SkipReasons {
  missingData: number;
  duplicate: number;
  error: number;
  alreadyOnBoard?: number;
  repeatedInFile?: number;
}

/**
 * Human, comma-joined breakdown of skip reasons (LEV-1044 n3), e.g.
 * "2 already in Orbit, 1 missing data, 1 error". Returns "" when nothing was
 * skipped for a known reason (caller decides whether to render a bare count).
 * Pure — no React, no DB.
 */
export function describeSkipReasons(
  reasons: SkipReasons | null | undefined,
): string {
  if (!reasons) return "";
  const parts: string[] = [];
  if (reasons.duplicate > 0) {
    parts.push(`${reasons.duplicate} already in Orbit`);
  }
  if (reasons.missingData > 0) {
    parts.push(`${reasons.missingData} missing data`);
  }
  if (reasons.error > 0) {
    parts.push(`${reasons.error} ${reasons.error === 1 ? "error" : "errors"}`);
  }
  return parts.join(", ");
}

/** Whether an import is still doing work (not yet terminal). */
export function isImportInProgress(status: string): boolean {
  return status === "pending" || status === "running";
}

/**
 * Format an import's timestamp for the history list (LEV-1044). Empty string for
 * a missing/invalid value; otherwise a date+time in the viewer's LOCAL timezone
 * (LEV-1076 — mirrors every other Orbit datetime; the history dialog is a
 * client-only component, so there's no SSR determinism to preserve). Pure — no
 * React, no DB.
 */
export function formatImportTimestamp(
  iso: string | null | undefined,
): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Human status line for an import-history row (LEV-1044). In-progress rows
 * surface their latest live status (the running step label, or a processed/total
 * fallback); terminal rows summarize the outcome. Pure — no React, no DB.
 */
export function describeImportHistoryStatus(item: {
  status: string;
  stepLabel: string | null;
  processed: number;
  total: number;
  addedCount: number;
  skippedCount: number;
  skipReasons?: SkipReasons | null;
  error: string | null;
}): string {
  switch (item.status) {
    case "pending":
      return "Queued…";
    case "running": {
      const step = item.stepLabel?.trim();
      if (step) return step;
      return item.total > 0
        ? `Importing ${item.processed}/${item.total}…`
        : "Importing…";
    }
    case "completed":
      return summarizeImport({
        kind: "csv",
        addedCount: item.addedCount,
        skippedCount: item.skippedCount,
        skipReasons: item.skipReasons,
        canceled: false,
      });
    case "canceled":
      return summarizeImport({
        kind: "csv",
        addedCount: item.addedCount,
        skippedCount: item.skippedCount,
        skipReasons: item.skipReasons,
        canceled: true,
      });
    case "failed":
      return item.error?.trim() || "Import failed.";
    default:
      return item.status;
  }
}

/** Whether the job should stop now because the owner explicitly canceled. */
export function shouldCancel(
  job: { cancelRequestedAt: Date | string | null },
): boolean {
  return job.cancelRequestedAt != null;
}

/**
 * Human summary for the terminal notification / modal banner. When a
 * `skipReasons` breakdown is supplied and rows were skipped, the skip clause
 * spells out WHY ("skipped 3 (2 already in Orbit, 1 missing data)"); otherwise
 * it falls back to a bare count.
 */
export function summarizeImport(args: {
  kind: PeopleImportKind;
  addedCount: number;
  skippedCount: number;
  canceled: boolean;
  skipReasons?: SkipReasons | null;
}): string {
  const noun = args.addedCount === 1 ? "person" : "people";
  const base = `Added ${args.addedCount} ${noun}`;
  let skip = "";
  if (args.skippedCount > 0) {
    const reasons = describeSkipReasons(args.skipReasons);
    skip = reasons
      ? `, skipped ${args.skippedCount} (${reasons})`
      : `, skipped ${args.skippedCount}`;
  }
  const tail = args.canceled ? " (canceled early)" : "";
  return `${base}${skip}${tail}.`;
}

/**
 * "Skipped 3 rows (1 missing data, 2 already on this board)." for an import
 * onto a pipeline board, or null when every row got a card. Counts exactly the
 * rows with no card: missing data, errors, people already on this board, and
 * rows repeated in the file. A person deduped against Orbit who still got a
 * card is never counted. With no reason breakdown this says nothing.
 */
export function pipelineImportSkipLine(
  skipReasons: SkipReasons | null | undefined,
): string | null {
  if (!skipReasons) return null;
  const onBoard = skipReasons.alreadyOnBoard ?? 0;
  const repeated = skipReasons.repeatedInFile ?? 0;
  const noCard = skipReasons.missingData + skipReasons.error + onBoard + repeated;
  if (noCard === 0) return null;
  const parts: string[] = [];
  if (skipReasons.missingData > 0) parts.push(`${skipReasons.missingData} missing data`);
  if (onBoard > 0) parts.push(`${onBoard} already on this board`);
  if (repeated > 0) parts.push(`${repeated} repeated in the file`);
  if (skipReasons.error > 0) {
    parts.push(`${skipReasons.error} ${skipReasons.error === 1 ? "error" : "errors"}`);
  }
  return `Skipped ${noCard} ${noCard === 1 ? "row" : "rows"} (${parts.join(", ")}).`;
}

/**
 * Done-panel summary for an import onto a pipeline board. States no added
 * count: the board's own "N new prospects added" counts the cards, and
 * `addedCount` counts new Orbit people, which is a different number.
 */
export function summarizePipelineImport(args: {
  skipReasons: SkipReasons | null | undefined;
  canceled: boolean;
}): string {
  const head = args.canceled ? "Import stopped early." : "Import finished.";
  const skip = pipelineImportSkipLine(args.skipReasons);
  return skip ? `${head} ${skip}` : head;
}

/** Feature-language label used in notifications + modal titles. */
export function importFeatureLabel(
  kind: PeopleImportKind,
  pipeline?: PipelineKind | null,
): string {
  if (kind === "apollo") {
    return pipeline === "application"
      ? "finding applicants with Apollo"
      : "finding prospects with Apollo";
  }
  if (kind === "orbit") {
    return pipeline === "application"
      ? "finding applicants in your Orbit"
      : "finding customers in your Orbit";
  }
  return "importing your contacts";
}
